<template>
	<slot name="trigger" :open="dropdownOpen" :open-dropdown="openDropdown" :close-dropdown="closeDropdown" :toggle-dropdown="toggleDropdown"></slot>
	<Teleport to="#app">
		<div class="fixed z-20 inset-0" v-cloak v-if="dropdownOpen" @click="closeDropdown"></div>
		<Transition
			enter-from-class="-translate-y-[calc(theme(spacing.88)_+_100%)] tab:-translate-y-[calc(theme(spacing.104)_+_100%)]"
			leave-to-class="-translate-y-[calc(theme(spacing.88)_+_100%)] tab:-translate-y-[calc(theme(spacing.104)_+_100%)]"
			enter-active-class="transition-transform duration-300 ease-out"
			leave-active-class="transition-transform duration-300 ease-in"
		>
			<div ref="dropdown" class="fixed inset-x-0 z-20 top-[calc(var(--nav-bar-height)_+_var(--subnav-bar-height))] focus-visible:outline-none after:absolute after:inset-0 after:pointer-events-none after:outline-0 focus-visible:after:outline-1 after:outline-dashed after:outline-gold-400/50 after:-outline-offset-4" v-cloak v-if="dropdownOpen" @click.capture="handleClick" tabindex="-1">
				<scrollable-container class="relative w-full max-h-[calc(var(--100vh)_-_var(--nav-bar-height)_-_var(--subnav-bar-height))] overflow-scroll bg-gray-900 border-t border-t-gray-800">
					<slot name="content" :close-dropdown="closeDropdown"></slot>
				</scrollable-container>
				<div class="absolute ~inset-x-32/104 top-1 h-32 bg-gradient-to-b from-gray-900 to-transparent pointer-events-none"></div>
				<div class="absolute ~inset-x-32/104 bottom-0 h-32 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none"></div>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from "vue";
import { lock, unlock } from "@js/body-scroll-lock.js";
import { create, activate, deactivate } from "@js/focus-trap.js";
import { store, mutations } from "@js/vue/store.js";

const dropdown = ref(null);
const focusTrap = ref(null);
const dropdownOpen = computed(() => store.navDropdownOpen);

function openDropdown() {
	mutations.openNavDropdown();
}

function closeDropdown() {
	mutations.closeNavDropdown();
}

function toggleDropdown() {
	mutations.toggleNavDropdown();
}

watch(dropdownOpen, async (newValue) => {
	if (newValue) {
		lock();
		await nextTick();
		focusTrap.value = create(dropdown.value);
		activate(focusTrap.value);
	} else {
		unlock();
		if (focusTrap.value) {
			deactivate(focusTrap.value);
		}
	}
}, { immediate: true });

function handleKeydown(e) {
	if (e.key === 'Escape' && dropdownOpen.value) {
		closeDropdown();
	}
}

window.addEventListener('keydown', handleKeydown);

onUnmounted(() => {
	window.removeEventListener('keydown', handleKeydown);
});

const handleClick = async (e) => {
	// Check if this is an artificial click event
	if (e.isTrusted === false) {
		return // Skip handling programmatic clicks
	}

	// Stop the original click event
	e.stopPropagation()
	e.preventDefault()

	const isClickable = e.target.closest('a, button');

	if (isClickable) {
		closeDropdown()
		// Wait for dropdown to close before proceeding
		await new Promise(resolve => setTimeout(resolve, 305));
	}

	// Get the original target
	const target = e.target
	target.click()
}
</script>
